import { CLUB_CATEGORIES, CLUB_CATEGORY_LABELS } from '@golf/common';
import type { ClubCategory } from '@golf/common';
import { Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api.ts';
import type { Club, ClubTemplate } from '../../lib/equipment.ts';
import { createClub, updateClub } from '../../lib/equipment.ts';
import { useSettings } from '../../lib/settings.tsx';
import type { DistanceUnit } from '../../lib/units.ts';
import { fromMetres, toMetres, unitLabel } from '../../lib/units.ts';

interface Props {
    club: Club | null;
    templates: ClubTemplate[];
    onClose: () => void;
    onSaved: () => void;
    /** Retirer ce club du matériel. Absent à la création : il n'y a rien à
     *  retirer d'un club qui n'existe pas encore. */
    onArchive?: (club: Club) => void;
}

type Form = {
    /** Le modèle du catalogue, porté jusqu'à l'enregistrement. */
    templateId: number | null;
    category: ClubCategory; label: string; nickname: string; brand: string; model: string;
    loftDeg: string; lieAngleDeg: string; shaftFlex: string; referenceDistanceM: string;
    purchasePrice: string; acquiredOn: string; notes: string;
};

const EMPTY: Form = {
    templateId: null,
    category: 'iron', label: '', nickname: '', brand: '', model: '',
    loftDeg: '', lieAngleDeg: '', shaftFlex: '', referenceDistanceM: '',
    purchasePrice: '', acquiredOn: '', notes: '',
};

/** La distance est stockée en mètres et saisie dans l'unité du joueur : elle
 *  est convertie à l'ouverture du formulaire, et reconvertie à l'envoi. Sans
 *  cela, un joueur en yards enregistrerait des yards pris pour des mètres. */
const fromClub = (c: Club, unit: DistanceUnit): Form => ({
    templateId: c.templateId,
    category: c.category,
    label: c.label,
    nickname: c.nickname ?? '',
    brand: c.brand ?? '',
    model: c.model ?? '',
    loftDeg: c.loftDeg?.toString() ?? '',
    lieAngleDeg: c.lieAngleDeg?.toString() ?? '',
    shaftFlex: c.shaftFlex ?? '',
    referenceDistanceM: c.referenceDistanceM != null
        ? String(fromMetres(c.referenceDistanceM, unit)) : '',
    purchasePrice: c.purchasePrice?.toString() ?? '',
    acquiredOn: c.acquiredOn?.slice(0, 10) ?? '',
    notes: c.notes ?? '',
});

/** Chaîne vide → null : un champ laissé vide est une absence de valeur, pas
 *  une chaîne vide, et la base a des contraintes là-dessus. */
const orNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));

export function ClubEditor({ club, templates, onClose, onSaved, onArchive }: Props) {
    const { unit } = useSettings();
    const [form, setForm] = useState<Form>(club ? fromClub(club, unit) : EMPTY);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setForm(club ? fromClub(club, unit) : EMPTY); }, [club, unit]);

    const set = <K extends keyof Form>(key: K, value: Form[K]): void =>
        setForm((f) => ({ ...f, [key]: value }));

    /** Pré-remplissage depuis le catalogue : nom, angle théorique et distance
     *  indicative. Tout reste modifiable — c'est l'angle réel du club qui
     *  compte, pas celui de la brochure. */
    const applyTemplate = (id: string): void => {
        const t = templates.find((x) => x.id === Number(id));
        if (!t) return;
        setForm((f) => ({
            ...f,
            // Le modèle choisi est RETENU, et non seulement recopié. C'est lui
            // que désignent les règles d'un exercice — « le fer 9 » — et le
            // formulaire le laissait tomber à l'enregistrement : un club nommé
            // « Le fidèle » serait resté sans type à jamais.
            templateId: t.id,
            category: t.category,
            label: t.label,
            loftDeg: t.defaultLoft?.toString() ?? '',
            // typicalCarryM est en mètres, comme son nom l'indique.
            referenceDistanceM: f.referenceDistanceM
                || (t.typicalCarryM != null ? String(fromMetres(t.typicalCarryM, unit)) : ''),
        }));
    };

    const submit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        const body = {
            templateId: form.templateId,
            category: form.category,
            label: form.label.trim(),
            nickname: orNull(form.nickname),
            brand: orNull(form.brand),
            model: orNull(form.model),
            loftDeg: numOrNull(form.loftDeg),
            lieAngleDeg: numOrNull(form.lieAngleDeg),
            shaftFlex: (orNull(form.shaftFlex) as 'L' | 'A' | 'R' | 'S' | 'X' | null),
            referenceDistanceM: form.referenceDistanceM.trim() === ''
                ? null : toMetres(Number(form.referenceDistanceM), unit),
            purchasePrice: numOrNull(form.purchasePrice),
            acquiredOn: orNull(form.acquiredOn),
            notes: orNull(form.notes),
        };
        try {
            if (club) await updateClub(club.id, body);
            else await createClub(body);
            onSaved();
            onClose();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Enregistrement impossible');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
            <div className="max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-box bg-base-200 sm:rounded-box">
                <div className="sticky top-0 flex items-center justify-between bg-base-200 px-4 py-3">
                    <h2 className="text-lg font-bold">{club ? 'Modifier le club' : 'Ajouter un club'}</h2>
                    <button type="button" className="btn btn-ghost btn-square" onClick={onClose} aria-label="Fermer">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={(e) => void submit(e)} className="space-y-3 px-4 pb-4">
                    {!club && (
                        <label className="form-control w-full">
                            <span className="label-text mb-1 block text-sm">Partir d'un modèle</span>
                            <select
                                className="select select-bordered w-full"
                                defaultValue=""
                                onChange={(e) => applyTemplate(e.target.value)}
                            >
                                <option value="">Saisie libre</option>
                                {templates.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.label}{t.defaultLoft != null ? ` — ${t.defaultLoft}°` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Famille</span>
                            <select
                                className="select select-bordered w-full"
                                value={form.category}
                                onChange={(e) => set('category', e.target.value as ClubCategory)}
                            >
                                {CLUB_CATEGORIES.map((c) => (
                                    <option key={c} value={c}>{CLUB_CATEGORY_LABELS[c]}</option>
                                ))}
                            </select>
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Nom *</span>
                            <input
                                className="input input-bordered w-full" required maxLength={60}
                                value={form.label} onChange={(e) => set('label', e.target.value)}
                                placeholder="Fer 7"
                            />
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Marque</span>
                            <input className="input input-bordered w-full" maxLength={60}
                                value={form.brand} onChange={(e) => set('brand', e.target.value)}
                                placeholder="TaylorMade" />
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Modèle</span>
                            <input className="input input-bordered w-full" maxLength={60}
                                value={form.model} onChange={(e) => set('model', e.target.value)}
                                placeholder="P790" />
                        </label>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Angle réel</span>
                            <input type="number" inputMode="decimal" step="0.5" min={1} max={75}
                                className="input input-bordered w-full"
                                value={form.loftDeg} onChange={(e) => set('loftDeg', e.target.value)}
                                placeholder="33.5" />
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Lie</span>
                            <input type="number" inputMode="decimal" step="0.5" min={30} max={90}
                                className="input input-bordered w-full"
                                value={form.lieAngleDeg} onChange={(e) => set('lieAngleDeg', e.target.value)}
                                placeholder="62" />
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Shaft</span>
                            <select className="select select-bordered w-full"
                                value={form.shaftFlex} onChange={(e) => set('shaftFlex', e.target.value)}>
                                <option value="">—</option>
                                {['L', 'A', 'R', 'S', 'X'].map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </label>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">
                                Distance ({unitLabel(unit)})
                            </span>
                            <input type="number" inputMode="numeric" min={1} max={450}
                                className="input input-bordered w-full"
                                value={form.referenceDistanceM}
                                onChange={(e) => set('referenceDistanceM', e.target.value)} />
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Prix (€)</span>
                            <input type="number" inputMode="decimal" step="0.01" min={0}
                                className="input input-bordered w-full"
                                value={form.purchasePrice}
                                onChange={(e) => set('purchasePrice', e.target.value)} />
                        </label>
                        <label className="form-control">
                            <span className="label-text mb-1 block text-sm">Acheté le</span>
                            <input type="date" className="input input-bordered w-full"
                                value={form.acquiredOn} onChange={(e) => set('acquiredOn', e.target.value)} />
                        </label>
                    </div>

                    <label className="form-control w-full">
                        <span className="label-text mb-1 block text-sm">Surnom</span>
                        <input className="input input-bordered w-full" maxLength={40}
                            value={form.nickname} onChange={(e) => set('nickname', e.target.value)}
                            placeholder="practice, secours…" />
                        <span className="mt-1 block text-xs opacity-60">
                            Utile seulement si deux clubs se ressemblent au point de ne plus se
                            distinguer : la marque et l'angle suffisent le plus souvent.
                        </span>
                    </label>

                    <label className="form-control w-full">
                        <span className="label-text mb-1 block text-sm">Notes</span>
                        <textarea className="textarea textarea-bordered w-full" rows={2} maxLength={1000}
                            value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                    </label>

                    {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

                    <div className="flex gap-2 pt-1">
                        <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Annuler</button>
                        <button type="submit" className="btn btn-primary flex-1 tap-target" disabled={busy}>
                            {busy ? <span className="loading loading-spinner" /> : 'Enregistrer'}
                        </button>
                    </div>

                    {/* Le retrait, en bas et en ligne de texte.
                        Il était une corbeille par ligne dans la liste : sur
                        quatorze clubs tenus d'une main gantée, cela faisait
                        quatorze occasions d'effacer le voisin. Ici, il faut
                        avoir ouvert la bonne fiche pour l'atteindre. */}
                    {club && onArchive && (
                        <button type="button"
                            onClick={() => { onArchive(club); onClose(); }}
                            className="btn btn-ghost btn-sm mx-auto mt-1 gap-2 font-semibold"
                            style={{ color: 'var(--brique-bord)' }}>
                            <Trash2 size={15} aria-hidden="true" /> Retirer ce club du matériel
                        </button>
                    )}
                </form>
            </div>
        </div>
    );
}
